import {Alert, Container, Divider, Group, Loader, Text, Title} from '@mantine/core'
import {Link} from '@tanstack/react-router'
import type {ReactNode} from 'react'

import {useCurrentUser} from 'src/auth/use-current-user'
import {useDispatchYards} from 'src/dispatch/use-dispatch'
import {canEditSales, editBlockedNote} from 'src/jobs/edit-rights'
import type {ProjectDetail} from 'src/projects/schemas'
import {useProject} from 'src/projects/use-projects'

type OrderPageFrameProps = {
    projectId: string
    title: (order: ProjectDetail) => string
    description: string
    children: (order: ProjectDetail, rights: {canEdit: boolean}) => ReactNode
}

/** Shared layout for project add pages. Loads the project and renders the read-only notice. */
export function OrderPageFrame({projectId, title, description, children}: OrderPageFrameProps) {
    const {data: order, isLoading, isError} = useProject(projectId)
    const currentUser = useCurrentUser()
    const {data: yards = []} = useDispatchYards()

    if (isLoading) {
        return (
            <Container size="lg" py="xl">
                <Group justify="center">
                    <Loader aria-label="Loading the order" />
                </Group>
            </Container>
        )
    }
    if (isError || !order) {
        return (
            <Container size="lg" py="xl">
                <Title order={1}>Order not found</Title>
                <Text c="dimmed" mt="sm">
                    This order may have been removed or the link is invalid.
                </Text>
                <Text component={Link} to="/projects" mt="md" size="sm">
                    Back to orders
                </Text>
            </Container>
        )
    }
    const owners = {repId: order.internalContact?.id, projectCreatedById: order.createdById}
    const workYardId = order.dispatchYardId ?? null
    const readOnlyNote = editBlockedNote({
        user: currentUser,
        owners,
        repName: order.internalContact?.name,
        workYardId,
        workYardName: yards.find((yard) => yard.id === workYardId)?.name,
        what: 'order',
    })
    return (
        <Container size="lg" py="xl">
            <Title order={1} mb="sm">
                {title(order)}
            </Title>
            <Text size="md" c="dimmed">
                {description}
            </Text>
            <Divider my="md" />
            {readOnlyNote ? (
                <Alert color="gray" variant="light" mb="md">
                    {readOnlyNote}
                </Alert>
            ) : null}
            {children(order, {canEdit: canEditSales(currentUser, owners, workYardId)})}
        </Container>
    )
}
